import {Event} from "../lib/constants/Event";
import {idGenerator} from '../lib/utils/IdGenerator'
import moment from 'moment'
import {DocumentNode} from "../lib/helpers/document/DocumentNode";
import {LinkBuilder} from "../lib/helpers/document/LinkBuilder";
import {assertThat, isDefined, isNotDefined, safeString} from "../lib/helpers/document/Functions";


const REMOVE = 'remove', ADD = 'add', SET = 'set'

// TODO Handle rules regarding document updates (Publish flow rules etc). If pubstart is missing, set the value to 'now' when stat:usable etc.
// TODO Validation of input, i.e status text should follow a certain pattern etc.


const isNotDefinedOrEmptyString = function (value) {
    return isNotDefined(value) || value.trim().length === 0
}

/**
 * Basic article handling that enables functionality as clearing and creating new copies of a newsitem article
 * @class
 * @alias api.article
 */
class Article {
    /** @hideconstructor */
    constructor(api) {
        this.api = api;
    }

    /**
     * Clear the article and create a new based on the configured base template.
     *
     * @param {boolean} disableWarning Optional, default false. If true, the article will be cleared without warning
     */
    clear(disableWarning) {
        if (!this.api.browser.getHash()) {
            this.api.browser.reload();
        }
        else {
            if (disableWarning) {
                this.api.events.trigger(
                    null,
                    Event.DISABLE_UNLOAD_WARNING,
                    {}
                )
            }

            this.api.browser.setHash('');
        }
    }

    /**
     * Create a new, unsaved, article based on the current article.
     *
     * @param {string} name Name or identifier of caller
     */
    copy(name) {
        if (!name) {
            name = '_internal' // Keep backwards compatibility where this was not necessary
        }

        // Create a new temporary id for this copy
        const tempId = '__temp__' + idGenerator()

        this.api.newsItem.setGuid(null);
        this.api.newsItem.removeDocumentURI();
        this.api.newsItem.setTemporaryId(tempId)
        this.api.newsItem.setHasPublishedVersion(name, false);

        this.api.browser.ignoreNextHashChange = true;
        this.api.browser.setHash('');
        this.api.ui.showNotification(
            'publish',
            null,
            this.api.getLabel('Copy created. You are now working on a new unsaved copy of the original article.')
        );
    }

    openInNewWindow(uuid) {
        this.api.browser.openInNewWindow({hash: uuid, url: 'current'});
    }

    getUrl(uuid) {
        return this.api.browser.getUrl({hash: uuid, url: 'current'})
    }

    /**
     * Sets the publication status for this document. The value is one of what is defined in the NewsML G2 standard:
     * https://www.iptc.org/std/NewsML-G2/latest/QuickStart-NewsML-G2-ItemBasics under the Publication Status section, together with
     * custom extension in the format [customExtensionPrefix]:[customValue], such as `imext:draft`
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.Article.setPubStatus({
     *      'change': 'stat:withheld'
     *   });
     *
     * <itemMeta>
     *   <pubStatus qcode="stat:withheld"/>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The value to be set in pubStatus. Expected value is in the form "stat:usable"
     * @param {string} [data.eventType] - Type of event to be sent when data has changed. default "document:changed"
     */
    setPubStatus({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change)) {
            throw new Error("Value must be defined for pubStatus")
        }

        assertThat(change).matches(/^\w+(?:\:\w+)?$/, "Pubstatus should match pattern 'xxx:yyy' or 'xxx'")

        this.itemMeta()
            .getOrCreate({element: 'pubStatus'})
            .setAttribute('qcode', change)
            .triggerEvent({eventType: eventType, action: SET, key: 'pubStatus', value: change})
    }


    /**
     * Adds a service to the itemMeta section of the article. The service tag concists of a mandatory 'qcode'
     * value and an optional 'why' attribute with extra information.
     *
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.addService({
     *     change: {
     *      qcode: "imchn:abc",
     *      why: "imext:main"
     *    }
     * });
     *
     *
     * <contentMeta>
     *     <service qcode="imchn:abc" why="imext:main"/>
     * </contentMeta>
     *
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The change specification
     * @param {string} data.change.qcode - The value to be set in the qcode for the service. Expected value is in the form "xxx:yyy"
     * @param {string} data.change.why - Optional parameter for the 'why' attribute for service.
     * @param {string} [data.eventType] - Type of event to be sent when data has changed. default "document:changed"
     */
    addService({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change) || isNotDefined(change.qcode)) {
            throw new Error("A value must be provided for service")
        }

        assertThat(change.qcode).matches(/^\w+(?:\:\w+)?$/, "Service qcode should match pattern 'xxx:yyy' or 'xxx'")

        this.itemMeta()
            .ifExists(`service[qcode="${change}"]`)
            .thenThrow('Service already exists')

        this.itemMeta()
            .create({
                element: 'service',
                attributeDefinitions: {
                    qcode: change.qcode
                }
            })
            .setAttributesFromFields({source: change, fields: ['why']})
            .triggerEvent({eventType: eventType, action: ADD, key: 'service', value: change})
    }


    /**
     * Removes a service from the itemMeta section of the article.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.removeService({
     *     change: "imchn:abc"
     * });

     *
     * @param {object} data - The change specification
     * @param {string} data.change - The qcode value matching the service to be removed. If there is no match, no event is sent.
     * @param {string} [data.eventType] - Type of event to be sent when data has changed. default "document:changed"
     */
    removeService({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change)) {
            throw new Error("A value must be provided for service")
        }

        assertThat(change).matches(/^\w+(?:\:\w+)?$/, "Service should match pattern 'xxx:yyy' or 'xxx'")

        this.itemMeta()
            .removeIfExists({selector: `service[qcode="${change}"]`})
            .triggerEvent({eventType: eventType, action: REMOVE, key: 'service', value: change})
    }


    /**
     * Sets the publish start time, using ISO_8601
     * standard. If value is null, pubStart will be unset.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setPubStart({
     *      'change': '2018-04-21T00:00:00+02:00'
     *   });
     * <itemMeta>
     *   <itemMetaExtProperty type="imext:pubstart" value="2018-04-21T00:00:00+02:00"/>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The time, in ISO_8601
     * @param {string} [data.eventType] - Type of event to send when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setPubStart({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isDefined(change)) {
            if (!moment(change, moment.ISO_8601).isValid()) {
                throw new Error('Unsupported date. It must be valid ISO 8601')
            }
        }

        this.setItemMetaExtProperty({
            change: {
                type: 'imext:pubstart',
                value: change
            },
            eventType
        })
    }

    /**
     * Sets the publish stop time, using ISO_8601 standard.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setPubStop({
     *      'change': '2018-04-21T00:00:00+02:00'
     *   });
     * <itemMeta>
     *   <itemMetaExtProperty type="imext:pubstop" value="2018-04-21T00:00:00+02:00"/>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The time, in ISO_8601
     * @param {string} [data.eventType] - Type of event to send when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setPubStop({change, eventType = Event.DOCUMENT_CHANGED}) {

        if (isDefined(change)) {
            if (!moment(change, moment.ISO_8601).isValid()) {
                throw new Error('Unsupported date. It must be valid ISO 8601')
            }
        }

        this.setItemMetaExtProperty({
            change: {
                type: 'imext:pubstop',
                value: change
            },
            eventType
        })
    }

    /**
     * Sets the value of the title.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setTitle({
     *      "change": "Document title"
     *   });
     * <itemMeta>
     *   <title>Document title</title>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - Value of the title to set. Must not be null or undefined
     * @param {object} [data.eventType] - Type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setTitle({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change)) {
            throw new Error("Value must be defined for title")
        }
        this.itemMeta()
            .getOrCreate({element: 'title'})
            .setText(safeString(change))
            .triggerEvent({eventType, action: SET, key: 'title', value: change})
    }

    /**
     * Sets the value of editor note
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setEdNote({
     *      "change": "Instructions to editor"
     *   });
     * <itemMeta>
     *   <edNote>Instructions to editor</edNote>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The text value of editor note. If null or empty, the element will be removed from document.
     * @param {object} [data.eventType] - Type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setEdNote({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.itemMeta(),
            change,
            elementName: 'edNote',
            eventType
        })

    }

    /**
     * Sets the value of a property. If value is null, the property will be removed.
     * The type of property to change is specified by the type argument.
     *
     * The type is defined by the [customPrefix]:[type] syntax, such as `imext:url`
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setItemMetaExtProperty({
     *      "change": {
     *          type: "imext:haspublishedversion",
     *          value: "true"
     *      }
     *   });
     * <itemMeta>
     *   <itemMetaExtProperty type="imext:haspublishedversion" value="true"/>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {object} data.change - Specification of the property to set.
     * @param {string} data.change.type - The identification of the property
     * @param {string} data.change.value - The value of the property. If null, the property will be removed.
     * @param {object} [data.eventType] - Type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setItemMetaExtProperty({change, eventType = Event.DOCUMENT_CHANGED}) {
        assertThat(change)
            .isDefined("'type' must be defined for itemMetaExtProperty")

        assertThat(change.type)
            .isDefined("'type' must be defined for itemMetaExtProperty")
            .matches(/^\w+\:\w+$/, "Unsupported syntax for 'type'. It should match pattern: 'xxx:yyy'")

        let propertySelector = `itemMetaExtProperty[type="${change.type}"]`;

        if (isNotDefined(change.value)) {
            this.itemMeta()
                .removeIfExists({selector: propertySelector})
                .triggerEvent({eventType, action: REMOVE, key: 'itemMetaExtProperty', value: change})
        } else {
            this.itemMeta()
                .getOrCreate({
                    element: 'itemMetaExtProperty',
                    selector: propertySelector,
                    attributeDefinitions: {
                        type: change.type,
                        value: change.value
                    }
                })
                .setAttribute("value", change.value)
                .triggerEvent({eventType, action: SET, key: 'itemMetaExtProperty', value: change})
        }
    }

    /**
     * Adds a link to the itemMeta section of the article.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.addItemMetaLink({
     *      change: {
     *          rel: "subject",
     *          title: "Mora",
     *          type: "x-im/category",
     *          uuid: "7b8461e8-4a22-11e8-aa12-b79c05b8e6d5",
     *          links: [
     *              {
     *                  rel: "broader",
     *                  title: "Dalarna",
     *                  uuid: "03d22994-91e4-11e5-8994-feff819cdc9f",
     *                  type: "x-im/category",
     *                  data: {
     *                      "placement": {
     *                          attributes: {
     *                              "type": "position"
     *                          },
     *                          value: "left"
     *                      }
     *                  }
     *              }
     *          ]
     *      }
     *   });
     *
     * <itemMeta>
     *   <links>
     *       <link rel="subject" title="Mora" type="x-im/category" uuid="7b8461e8-4a22-11e8-aa12-b79c05b8e6d5">
     *           <links>
     *               <link rel="broader" title="Dalarna" uuid="03d22994-91e4-11e5-8994-feff819cdc9f" type="x-im/category">
     *                   <data>
     *                       <placement type="position>left</placement>
     *                   </data>
     *           </links>
     *       </link>
     *   </links>
     * </itemMeta>
     *
     * @param {object} data - The change specification
     * @param {object} data.change - Specifies the link to be created.
     * @param {string} data.change.rel - The 'rel' attribute of the link. Example: "author". This parameter is mandatory and must be defined. Must not contain spaces
     * @param {string} data.change.type - The 'type' attribute of the link. Example: "x-im/person". This parameter is mandatory and must be defined.
     * @param {string} data.change.uri - The 'uri' pointing to an external resource. This OR the 'uuid' parameter must be present.
     * @param {string} data.change.uuid - The 'uuid', which is an ID of a resource in the writer repository. This OR the 'uri' parameter must be present.
     * @param {string} data.change.data - An optional data block with custom tags.
     * @param {array} data.change.links - Optional links for the link to be created. Should be of the same format as the top-most link.
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    addItemMetaLink({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change) || isNotDefined(change.rel) || isNotDefined(change.type)) {
            throw new Error("'rel' and 'type' must be specified for link")
        }

        if (isNotDefined(change.uuid) && isNotDefined(change.uri)) {
            throw new Error("'uuid' or 'uri' must be specified for link")
        }

        const identifier = change.uuid ? `uuid="${change.uuid}"` : `uri="${change.uri}"`

        this.itemMeta()
            .ifExists(`links > link[rel="${change.rel}"][${identifier}]`)
            .thenThrow(`A link with rel="${change.rel}" and ${identifier} already exist`)

        const links = this.itemMeta().getOrCreate({element: 'links'})

        this.linkBuilder(links)
            .validate(change)
            .execute(change)

        links.triggerEvent({eventType, action: ADD, key: 'itemMetaLink', value: change})
    }

    /**
     * Removes a link from itemMeta section
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.removeItemMetaLink({
     *      change: {
     *          rel: 'subject',
     *          uuid: '320938f8-db94-11e5-b5d2-0a1d41d68578'
     *      }
     *   });
     *
     * @param {object} data - The change specification
     * @param {object} data.change - The link to remove
     * @param {string} data.rel - The rel attribute of the link
     * @param {string} data.uri - The URI that identifies the link, OR
     * @param {string} data.uuid - The uuid that identifies the link.
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    removeItemMetaLink({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change) || isNotDefined(change.rel)) {
            throw new Error("'rel' must be specified for link")
        }
        if (isNotDefined(change.uuid) && isNotDefined(change.uri)) {
            throw new Error("'uuid' or 'uri' must be specified for link")
        }

        const identifier = change.uuid ? `uuid="${change.uuid}"` : `uri="${change.uri}"`

        let linksNode = this.itemMeta('links')

        linksNode
            .removeIfExists({selector: `link[rel="${change.rel}"][${identifier}]`})
            .triggerEvent({eventType, action: REMOVE, key: 'itemMetaLink', value: change})
    }

    /**
     * Sets the alternative ID for the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setAltId({
     *      "change": "854575"
     *   });
     *
     * <contentMeta>
     *   <altId>854575</altId>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The new ID to set
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setAltId({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.contentMeta(),
            change,
            elementName: 'altId',
            eventType
        })

    }

    /**
     * Sets the slugline for the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setSlugline({
     *      "change": "This article is all about"
     *   });
     *
     * <contentMeta>
     *   <slugline>This article is all about</slugline>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The new slugline
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setSlugline({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.contentMeta(),
            change,
            elementName: 'slugline',
            eventType
        })

    }

    /**
     * Sets the description of the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setDescription({
     *      "change": "Description of the article"
     *   });
     *
     * <contentMeta>
     *   <description>Description of the article</description>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The new description
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setDescription({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.contentMeta(),
            change,
            elementName: 'description',
            eventType
        })
    }

    /**
     * Sets the language for the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setLanguage({
     *      "change": "en"
     *   });
     *
     * <contentMeta>
     *   <language tag="en"/>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The language to set
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setLanguage({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefinedOrEmptyString(change)) {
            this.contentMeta()
                .removeIfExists({element: 'language'})
                .triggerEvent({eventType, action: REMOVE, key: 'language', value: null})
        } else {
            this.contentMeta()
                .getOrCreate({element: 'language'})
                .setAttribute('tag', change)
                .triggerEvent({eventType, action: SET, key: 'language', value: change})
        }
    }


    /**
     * Sets the language in the `idf` section of the document. See https://www.w3.org/2005/05/font-size-test/starhtml-test.html for
     * valid language codes.
     * @param {object} data - The change specification
     * @param {string} data.change - The specification of language and optional direction
     * @param {string} data.change.lang - The language specification, as specified in the link above.
     * @param {string} data.change.dir - The text direction
     *
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setIdfLanguage({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change) || isNotDefined(change.lang)) {
            throw new Error("'lang' must be specified for idfLanguage")
        }

        assertThat(change.lang).matches(/^(?:[xi]-{1}?|[a-z]{2,3}$|[a-z]{2,3}-{1}?)(?:-[a-zA-Z0-9]{1,8})*/, 'Illegal syntax for language. Should follow syntax specified in https://www.w3.org/2005/05/font-size-test/starhtml-test.html')

        if (change.dir) {
            assertThat(change.dir).matches(/ltr|rtl|auto/, "Illegal syntax for direction. Should be one of 'ltr', 'rtl', 'auto'")
        }

        const idf = this.documentNode('newsItem > contentSet > inlineXML > idf')

        idf
            .setAttributes({
                attributeDefinitions: {
                    'xml:lang': change.lang,
                    'dir': change.dir || 'ltr'
                }
            })
            .triggerEvent({eventType, action: SET, key: 'idfLanguage', value: change})

    }

    /**
     * Sets the byline for the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setBy({
     *      "change": "Boris Byline"
     *   });
     *
     * <contentMeta>
     *   <by>Boris Byline</by>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The new byline
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setBy({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.contentMeta(),
            change,
            elementName: 'by',
            eventType
        });
    }

    /**
     * Sets the headline of the article. If null, the element will be removed.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.setHeadline({
     *      "change": "Article headline"
     *   });
     *
     * <contentMeta>
     *   <headline>Article headline</headline>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {string} data.change - The new headline
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    setHeadline({change, eventType = Event.DOCUMENT_CHANGED}) {
        this.setOrRemoveTextNode({
            node: this.contentMeta(),
            change,
            elementName: 'headline',
            eventType
        })
    }

    /**
     * Adds a link to the contentMeta section of the article.
     *
     * @example
     * import {api} from 'writer'
     *
     * api.article.addContentMetaLink({
     *   change: {
     *     rel: "articlesource",
     *     title: "Online",
     *     type: "x-im/articlesource",
     *     uri: "im://articlesource/online"
     *   }
     * });
     *
     * <contentMeta>
     *     <links>
     *         <link rel="articlesource" title="Online" type="x-im/articlesource" uri="im://articlesource/online"/>
     *     </links>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {object} data.change - Specifies the link to be created.
     * @param {string} data.change.rel - The 'rel' attribute of the link. Example: "author". This parameter is mandatory and must be defined.
     * @param {string} data.change.type - The 'type' attribute of the link. Example: "x-im/person". This parameter is mandatory and must be defined.
     * @param {string} data.change.uri - The 'uri' pointing to an external resource. This OR the 'uuid' parameter must be present.
     * @param {string} data.change.uuid - The 'uuid', which is an ID of a resource in the writer repository. This OR the 'uri' parameter must be present.
     * @param {string} data.change.data - An optional data block with custom tags.
     * @param {array} data.change.links - Optional links for the link to be created. Should be of the same format as the top-most link.
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    addContentMetaLink({change, eventType = Event.DOCUMENT_CHANGED}) {
        assertThat(change).isDefined()

        if (isNotDefined(change.uuid) && isNotDefined(change.uri)) {
            throw new Error("'uuid' or 'uri' must be specified for link")
        }

        assertThat(change.rel)
            .isDefined("'rel' must be specified for link")
            .matches(/^\S+$/, "\"Unsupported syntax for 'rel' argument. It should match pattern 'xxxxx'\"")
        assertThat(change.type)
            .isDefined("'type' must be specified for link")
            .matches(/^[\w-]+(?:\/\w+)+$/, "Unsupported syntax for 'type' argument. It should match pattern 'xxx/yy' or 'xxxx'")
        assertThat(change.uri)
            .whenDefined()
            .matches(/^\S+$/, "Unsupported syntax for 'uri' argument. It should match pattern 'xxxx'")
        assertThat(change.uuid)
            .whenDefined()
            .matches(/^\S+$/, "Unsupported syntax for 'uuid' argument. It should match pattern 'xxxx'")

        const identifier = change.uuid ? `uuid="${change.uuid}"` : `uri="${change.uri}"`

        this.contentMeta()
            .ifExists(`links > link[rel="${change.rel}"][${identifier}]`)
            .thenThrow(`A link with rel="${change.rel}" and ${identifier} already exist`)

        const links = this.contentMeta().getOrCreate({element: 'links'})

        this.linkBuilder(links)
            .validate(change)
            .execute(change)

        links.triggerEvent({eventType, action: ADD, key: 'contentMetaLink', value: change})
    }

    /**
     * Removes a link from contentMeta section
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.removeContentMetaLink({
     *      change: {
     *          rel: 'subject',
     *          uuid: '320938f8-db94-11e5-b5d2-0a1d41d68578'
     *      }
     *   });
     *
     * @param {object} data - The change specification
     * @param {object} data.change - The link to remove
     * @param {string} data.rel - The rel attribute of the link
     * @param {string} data.uri - The URI that identifies the link, OR
     * @param {string} data.uuid - The uuid that identifies the link.
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    removeContentMetaLink({change, eventType = Event.DOCUMENT_CHANGED}) {
        assertThat(change).isDefined()
        assertThat(change.rel).isDefined("'rel' must be specified for link")

        if (isNotDefined(change.uuid) && isNotDefined(change.uri)) {
            throw new Error("'uuid' or 'uri' must be specified for link")
        }

        const identifier = change.uuid ? `uuid="${change.uuid}"` : `uri="${change.uri}"`

        let linksNode = this.contentMeta('links')

        linksNode
            .removeIfExists({selector: `link[rel="${change.rel}"][${identifier}]`})
            .triggerEvent({eventType, action: REMOVE, key: 'contentMetaLink', value: change})
    }

    /**
     * Adds an object element to the metadata section in contentMeta
     *
     * @example
     * import {api} from 'writer'
     *
     * api.article.addContentMetadata({
     *     change: {
     *         id: "8400c74d667e",
     *         type: "x-im/teaser",
     *         title: "Sed sit amet turpis a purus fringilla",
     *         data: {
     *             subject: "In sodales lectus vel egestas rhoncus",
     *             text: "Duis eget magna lacus. In sodales lectus vel egestas rhoncus."
     *         }
     *         links: [
     *             {
     *                 rel: "image",
     *                 type: "x-im/image",
     *                 uri: "im://image/znX8U1CU124n26zu7gb40_jBzSk.jpeg",
     *                 uuid: "c382c937-8511-5d48-9677-55658c2bbb32",
     *                 data: {
     *                     width: "1536",
     *                     height: "1024"
     *                 }
     *             }
     *         ]
     *     }
     * })
     *
     * <contentMeta>
     *     <metadata>
     *         <object id="8400c74d667e" type="x-im/teaser" title="Sed sit amet turpis a purus fringilla">
     *            <data>
     *                <subject>In sodales lectus vel egestas rhoncus</subject>
     *                <text>Duis eget magna lacus. In sodales lectus vel egestas rhoncus.</text>
     *            </data>
     *            <links>
     *                <link rel="image"
     *                          type="x-im/image"
     *                          uri="im://image/znX8U1CU124n26zu7gb40_jBzSk.jpeg"
     *                          uuid="c382c937-8511-5d48-9677-55658c2bbb32">
     *                    <data>
     *                        <width>1536</width>
     *                        <height>1024</height>
     *                    </data>
     *                </link>
     *            </links>
     *         </object>
     *     </metadata>
     * </contentMeta>
     *
     * @param {object} data - The change specification
     * @param {object} data.change - The specification of the object tag to add
     * @param {string} data.change.id - A mandatory id of the object
     * @param {string} data.change.type - A mandatory type of object
     * @param {string} data.change.title - A an optional title of the object
     * @param {object} data.change.data - Optional data element, with custom tags.
     * @param {array} data.change.links - Optional array with link elements. The link follows the same format as with #addContentMetaLink
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    addContentMetadata({change, eventType = Event.DOCUMENT_CHANGED}) {
        if (isNotDefined(change) || isNotDefined(change.id) || isNotDefined(change.type)) {
            throw new Error("'id' and 'type' must be specified for metadata")
        }

        assertThat(change).isDefined()
        assertThat(change.id).isDefined("'id' must be specified for contentMetadata")
        assertThat(change.type).isDefined("'type' must be specified for contentMetadata")

        const metaDataNode = this.contentMeta()
            .getOrCreate({element: 'metadata'})

        metaDataNode
            .ifExists(`object[id="${change.id}"]`)
            .thenThrow('Object already exists')

        const objectNode = metaDataNode.create({element: 'object', attributeDefinitions: {id: change.id, type: change.type}})

        objectNode.setAttributesFromFields({source: change, fields: ['title']})

        if (change.data) {
            const dataNode = objectNode.create({element: 'data'})
            Object.entries(change.data).forEach(([key, value]) => {
                if (value.attributes && value.value) {
                    dataNode.create({element: key, attributeDefinitions: value.attributes}).setText(value.value)
                } else {
                    dataNode.create({element: key}).setText(value)
                }
            })
        }

        if (change.links) {

            const links = objectNode.getOrCreate({element: 'links'})

            change.links.forEach((link) => {
                this.linkBuilder(links).validate(link).execute(link)
            })
        }

        objectNode.triggerEvent({eventType, action: ADD, key: 'contentMetadata', value: change})
    }

    /**
     * Removes an object in the contentMeta metadata section of the article.
     *
     * @Example
     * import {api} from 'writer'
     *
     * api.article.removeContentMetadata({
     *      change: {
     *          id: 'A8567B335'
     *      }
     *   });
     *
     * @param {object} data - The change specification
     * @param {object} data.change - The specification of the object tag to remove
     * @param {string} data.change.id - The ID for the tag to remove. This argument is required
     * @param {string} [data.eventType] - The type of event to be sent when data has changed. Default Event.DOCUMENT_CHANGED
     */
    removeContentMetadata({change, eventType = Event.DOCUMENT_CHANGED}) {
        assertThat(change).isDefined()
        assertThat(change.id).isDefined("'id' must be specified for contentMetadata")

        this.contentMeta('metadata')
            .removeIfExists({selector: `object[id="${change.id}"]`})
            .triggerEvent({eventType, action: REMOVE, key: 'contentMetadata', value: change})
    }

    // =======
    //  Utils
    // =======

    setOrRemoveTextNode({node, change, elementName, eventType}) {
        assertThat(node).isDefined("missing 'node' object parameter")
        assertThat(elementName).isDefined("missing 'elementName' object parameter")

        if (isNotDefinedOrEmptyString(change)) {
            node
                .removeIfExists({element: elementName})
                .triggerEvent({eventType, action: REMOVE, key: elementName, value: null})
        } else {
            node
                .getOrCreate({element: elementName})
                .setText(change)
                .triggerEvent({eventType, action: SET, key: elementName, value: change})
        }
    }

    /**
     * Returns the itemMeta node
     *
     * @param path Optional path to find node under itemMeta
     * @return {DocumentNode} A node instance
     * @throws Error when node does not exist
     */
    itemMeta(path = '') {
        const selector =
            'newsItem > itemMeta' +
            (path.length === 0 ? '' : ` > ${path}`)
        return this.documentNode(selector);
    }

    /**
     * Returns the contentMeta node
     *
     * @param path Optional path to find node under contentMeta
     * @return {DocumentNode} A node instance
     * @throws Error when node does not exist
     */
    contentMeta(path = "") {
        const selector =
            'newsItem > contentMeta' +
            (path.length === 0 ? '' : ` > ${path}`)
        return this.documentNode(selector);
    }

    documentNode(selector) {
        let node = this.api.newsItemArticle.querySelector(selector);

        if (!node) {
            throw new Error("No node to operate on for selector: " + selector)
        }

        return new DocumentNode(this.api.events, this.api.newsItemArticle, node)
    }

    linkBuilder(linksNode) {
        return new LinkBuilder(linksNode)
    }
}

export {Article}
